import React, { useMemo, useRef } from "react";
import RuleArtifactCard from "./RuleTree/RuleChat/RuleArtifactCard";
import "./RuleTree/ruleAgentTheme.css";

/* The rule engine's half of the SDK's renderer contract. The agent ends an accepted emit_rule by
   appending a fenced block to its answer:

       ```qi-rule-artifact
       {"title": "Validação de score mínimo", "rule": {"tree_data": [...]}}
       ```

   The SDK matches the identifier and hands the body over verbatim; everything below — parsing it,
   judging it, drawing it — belongs here. The card itself is the one the old chat used, unchanged,
   so what the user sees and drags is what they had before. */

export const RULE_ARTIFACT_IDENTIFIER = "qi-rule-artifact";

function noInsert() {}

function RuleArtifactBlock({ content, host }) {
    /* Throwing is the documented way to decline: the SDK answers it with the monospace block, so a
       payload the model got wrong stays readable instead of leaving a hole in the conversation. */
    let artifact = JSON.parse(content);
    if (!artifact || typeof artifact !== "object" || !artifact.rule) {
        throw new Error("qi-rule-artifact carries no rule object");
    }

    let { rulesContext, userVariablesAvailable, onInsertNodes } = host.current;
    return (
        <RuleArtifactCard
            artifact={artifact}
            rulesContext={rulesContext}
            userVariablesAvailable={userVariablesAvailable}
            onInsertNodes={typeof onInsertNodes === "function" ? onInsertNodes : noInsert}
        />
    );
}

/* The array is built once and never again. The SDK carries it down a context, so a fresh array on
   every render would redraw every fenced block of the conversation on every keystroke of a stream.
   A ref is what lets the identity stay put while the values the card needs stay current — the
   engines rebuild `onInsertNodes` and `userVariablesAvailable` on each of their own renders. */
export function useRuleArtifactRenderers({ rulesContext, userVariablesAvailable, onInsertNodes }) {
    const host = useRef({});
    host.current = { rulesContext, userVariablesAvailable, onInsertNodes };

    return useMemo(
        () => [
            {
                identifier: RULE_ARTIFACT_IDENTIFIER,
                renderer: ({ content }) => <RuleArtifactBlock content={content} host={host} />
            }
        ],
        []
    );
}
